import { useLocation, useNavigate } from "react-router-dom";
import { ChangeEventStatus, SaveChangeEventInput } from "src/generated/graphqlTypes";
import { WorkflowActionArgs, WorkflowFormState } from "src/routes/projects/change-events/guided/workflows/types";
import { createChangeEventsUrl, createGuidedChangeEventUrl } from "src/RouteUrls";
import { BaseChangeEventFormInput, BaseChangeEventFormState } from "../../baseChangeEventForm";
import { useSaveGuidedChangeEventMutation } from "./useGuidedChangeEventActions.generated";

/**
 * Defines the save actions a guided change event workflow shares.
 *
 * saveChangeEvent:
 * - If new, creates a new draft change event
 * - Saves the base change-event fields, plus whatever the workflow's own `customSaveInput` contributes,
 * e.g. Swap Product's staged swaps.
 * - Folds the saved change event back into the form, so the next advance doesn't re-send everything.
 * - Or, as a dry run, only previews what the save would derive: the backend rolls it back, and the response's change
 * event lives in Apollo's cache alone, where the downstream steps read it by `dryRunChangeEventId`.
 *
 * @param args - The workflow action arguments.
 * @param customSaveInput - Defines additional fields to save along with the base change event fields. e.g. Swap Product's staged swaps.
 */
export function useGuidedChangeEventActions<F extends BaseChangeEventFormInput>(
  args: WorkflowActionArgs<F>,
  customSaveInput: (form: WorkflowFormState<F>) => Partial<SaveChangeEventInput> = () => ({}),
) {
  const { form, projectId } = args;
  const baseForm = form as typeof form & BaseChangeEventFormState<F>;
  const navigate = useNavigate();
  const { state } = useLocation();
  const [saveChangeEvent] = useSaveGuidedChangeEventMutation();

  return {
    /**
     * Saves the change event.
     * @param andExit - If true, the user will be redirected to the change events page after saving.
     * @param force - If true, the change event will be saved even if it's not dirty.
     * @param dryRun - If true, nothing is persisted: the backend derives the change event and rolls it back, and its
     * id is kept on the form as `dryRunChangeEventId` so the steps can read the preview from the cache.
     */
    saveChangeEvent: async ({
      andExit,
      force = false,
      dryRun = false,
    }: { andExit?: boolean; force?: boolean; dryRun?: boolean } = {}): Promise<void> => {
      if (!force) {
        if (!form.canSave()) return;
        // Nothing to save, but "exit" still has to exit, otherwise Save & Exit is a dead button on any
        // step the user only read through.
        if (!form.dirty) {
          if (andExit) navigate(createChangeEventsUrl(projectId));
          return;
        }
      }
      // The same input derives the same preview, so don't re-run it (and burn another phantom id) on every
      // Continue; picking a different configuration clears the id, which is what forces a new dry run.
      if (dryRun && baseForm.dryRunChangeEventId.value) return;
      const isNew = !baseForm.id.value;
      const result = await saveChangeEvent({
        variables: {
          structureId: projectId,
          input: { ...toSaveChangeEventWorkflowInput(form, customSaveInput), ...(dryRun ? { dryRun } : {}) },
        },
      });
      const savedChangeEvent = result.data?.saveChangeEvent.changeEvent;
      if (!savedChangeEvent) return;

      if (dryRun) {
        // Only remember the preview's id. Folding the response into the form would make its phantom id the form's
        // `id`, i.e. "the draft exists": submit would send it to the server, and the unsaved-changes guard would go
        // quiet. The form stays dirty, as it does before any save.
        baseForm.dryRunChangeEventId.set(savedChangeEvent.id);
        return;
      }
      // Fold the saved change event back into the form: `update` re-runs the workflow's `mapToForm`
      // so the rows pick up their new swap ids and the form goes clean. Without it, the next advance
      // would re-send rows the backend already deleted and re-created.
      form.revertChanges();
      form.update(savedChangeEvent);
      // A real save supersedes any preview, so a stale phantom id can't be read by the steps
      baseForm.dryRunChangeEventId.set(undefined);
      // If new, replace the current url with the new change event id
      if (isNew) {
        navigate(createGuidedChangeEventUrl(projectId, savedChangeEvent.id), { replace: true, state });
      }
      if (andExit) {
        navigate(createChangeEventsUrl(projectId));
      }
    },
  };
}

/**
 * The draft change event a guided workflow's form describes, i.e. the base change-event fields plus whatever the
 * workflow's own `customSaveInput` contributes.
 */
export function toSaveChangeEventWorkflowInput<F extends BaseChangeEventFormInput>(
  form: WorkflowFormState<F>,
  customSaveInput: (form: WorkflowFormState<F>) => Partial<SaveChangeEventInput> = () => ({}),
): SaveChangeEventInput {
  const baseForm = form as typeof form & BaseChangeEventFormState<F>;
  return {
    id: baseForm.id.changedValue ?? undefined,
    // Don't send title unless it's been changed, because it's auto-generated on the backend.
    title: baseForm.title.dirty ? baseForm.title.changedValue : undefined,
    workflow: baseForm.workflow.value,
    projectStageId: baseForm.projectStageId.value,
    reasonId: baseForm.reasonId.value,
    type: baseForm.type.value,
    status: ChangeEventStatus.Draft,
    hasPreSignedContract: baseForm.hasPreSignedContract.value,
    documentId: baseForm.documentId.value,
    ...customSaveInput(form),
  };
}
